import React, { FC, useCallback, useEffect, useRef } from 'react';
import Grabber from '../grabber';
import type { KonvaEventObject } from 'konva/lib/Node';

type Point = { x: number; y: number };

interface VerticalHandleProps {
    /** Background-image pixel-space position (unscaled) — `x` moves on drag, `y` just follows
     * wherever the measure line currently is. */
    x: number;
    y: number;
    scale: number;
    offset: Point;
    cursorChange: (cursor: string) => void;
    onDragMove: (x: number) => void;
    onDragEnd: () => void;
}

/**
 * One of the two draggable left/right-edge markers, constrained to horizontal movement only.
 * Mirrors the mouse-event-based dragging used in `moveGrabbers.tsx` so re-renders mid-drag don't lose the gesture.
 */
const VerticalHandle: FC<VerticalHandleProps> = ({ x, y, scale, offset, cursorChange, onDragMove, onDragEnd }) => {
    const dragRef = useRef<{
        startX: number;
        clientX: number;
        onDragMove: (x: number) => void;
        onDragEnd: () => void;
        scale: number;
    }>({
        startX: x,
        clientX: 0,
        onDragMove,
        onDragEnd,
        scale,
    });

    dragRef.current = {
        startX: dragRef.current.startX,
        clientX: dragRef.current.clientX,
        onDragMove,
        onDragEnd,
        scale,
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        const { startX, clientX, scale: currentScale, onDragMove: currentOnDragMove } = dragRef.current;
        const deltaX = (clientX - e.clientX) / currentScale;
        currentOnDragMove(startX - deltaX);
    }, []);

    const handleMouseUp = useCallback(() => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        dragRef.current.onDragEnd();
    }, [handleMouseMove]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    const onDragStart = useCallback(
        (e: KonvaEventObject<MouseEvent>) => {
            dragRef.current.startX = x;
            dragRef.current.clientX = e.evt.clientX;
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [x, handleMouseMove, handleMouseUp],
    );

    const position = { x: x * scale + offset.x, y: y * scale + offset.y };
    return <Grabber position={position} cursorChange={cursorChange} onDragStart={onDragStart} />;
};

export default VerticalHandle;
